import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { TESTIDS } from "@/lib/testIds";
import BrandMark from "@/components/site/Wordmark";
import { setPathway } from "@/lib/pathway";

/**
 * ImpactVest primary navigation.
 *
 * Design contract (see /app/design_guidelines.md · Nav Redesign v2):
 * - Two explicit header states: `data-scrolled="false"` (over cream) and
 *   `data-scrolled="true"` (over charcoal). One shared state variable drives
 *   every foreground color via CSS on the .iv-nav-* utilities.
 * - Navigation: For Investors · For Companies · How it works.
 * - Right side: Sign in (link) · Begin (primary CTA).
 * - `Pilot` is intentionally removed from the primary nav.
 * - Active section highlight is driven by IntersectionObserver on `.iv-anchor`
 *   sections in the document.
 * - Mobile: full-screen restrained menu with focus trap, ESC dismissal, body
 *   scroll-lock, safe-area padding, and focus restoration to the toggle.
 */

const NAV = [
  { label: "How it works", href: "/#how", testid: TESTIDS.header.navOverview },
];

// Map href → anchor id for the active-section observer
const ANCHOR_IDS = NAV.map((n) => n.href.replace(/^\/?#/, ""));

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("top");
  const location = useLocation();
  const headerRef = useRef(null);
  const toggleRef = useRef(null);
  const firstMobileLinkRef = useRef(null);
  const closeBtnRef = useRef(null);

  // ---- scroll state + active section (single source of truth) ----
  // We deliberately do NOT use IntersectionObserver for the active state:
  // IO fires on ratio thresholds, which for sections of very different heights
  // causes the "third menu item lights up before the third section is reached"
  // bug. Instead we compute active on scroll from the actual DOM geometry:
  // the active section is the LAST one whose top edge has crossed the sticky
  // header's bottom line (plus a small tolerance). This is deterministic,
  // handles short sections correctly and stays in sync with the nav order.
  useEffect(() => {
    const compute = () => {
      // header state
      const y = window.scrollY;
      setScrolled(y > 24);

      // measure current header height and publish as CSS var so
      // scroll-margin-top / anchor offsets stay accurate on every viewport.
      const h = headerRef.current?.offsetHeight || 72;
      document.documentElement.style.setProperty("--iv-header-h", `${h}px`);

      // active section: last one with top ≤ headerBottom + tolerance
      const boundary = h + 24; // 24px tolerance
      let current = "top";
      for (const id of ANCHOR_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - boundary <= 1) current = id;
        else break; // ANCHOR_IDS is in document order; stop at the first not-yet-reached
      }
      // If page has been scrolled to the very bottom, force the last item active
      const nearBottom = window.innerHeight + y >= document.documentElement.scrollHeight - 4;
      if (nearBottom) current = ANCHOR_IDS[ANCHOR_IDS.length - 1] || current;
      setActive(current);
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [location.pathname]);

  // ---- close mobile menu on route change ----
  useEffect(() => setOpen(false), [location.pathname]);

  // ---- initial-load / back-forward hash sync ----
  // When the page loads with a #hash (refresh, direct link, back button),
  // native anchor jumps happen BEFORE our --iv-header-h CSS var is set,
  // so we re-scroll with the exact offset once mounted.
  useEffect(() => {
    const syncFromHash = () => {
      const h = window.location.hash.replace(/^#/, "");
      if (!h) return;
      const el = document.getElementById(h);
      if (!el) return;
      const hh = headerRef.current?.offsetHeight || 72;
      const top = el.getBoundingClientRect().top + window.scrollY - hh - 16;
      window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
    };
    // let the browser finish its own scroll first
    const t = window.setTimeout(syncFromHash, 30);
    window.addEventListener("hashchange", syncFromHash);
    window.addEventListener("popstate", syncFromHash);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("hashchange", syncFromHash);
      window.removeEventListener("popstate", syncFromHash);
    };
  }, []);

  // ---- mobile menu: body scroll-lock + ESC + focus trap + restore focus ----
  const closeMenu = useCallback(() => {
    setOpen(false);
    // Restore focus to the toggle
    requestAnimationFrame(() => toggleRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    // Prevent layout shift from scrollbar disappearing
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;

    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); closeMenu(); return; }
      if (e.key !== "Tab") return;
      // Simple focus trap inside the mobile panel
      const panel = document.getElementById("iv-mobile-nav");
      if (!panel) return;
      const focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    // Move focus into the panel (close button) shortly after open
    requestAnimationFrame(() => closeBtnRef.current?.focus());

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, closeMenu]);

  const handleAnchor = useCallback((href) => (e) => {
    // Same-page anchor navigation — smooth-scroll + close mobile menu.
    // We compute the absolute Y offset ourselves so we can subtract the
    // exact sticky-header height (measured at runtime), guaranteeing the
    // section heading sits just below the header regardless of viewport.
    if (!href.startsWith("/#")) return;
    if (location.pathname === "/") {
      e.preventDefault();
      const id = href.slice(2);
      const el = document.getElementById(id);
      if (el) {
        const h = headerRef.current?.offsetHeight || 72;
        const top = el.getBoundingClientRect().top + window.scrollY - h - 16;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        // Update the URL hash without a hard jump (keeps back/forward working)
        window.history.pushState(null, "", `#${id}`);
        // Optimistically set active — the scroll listener will confirm
        setActive(id);
      }
    }
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      ref={headerRef}
      data-testid={TESTIDS.header.root}
      data-scrolled={scrolled ? "true" : "false"}
      className="iv-header fixed top-0 inset-x-0 z-50 transition-colors duration-300"
    >
      <div className="iv-container flex items-center justify-between h-[68px] md:h-[72px]">
        {/* Brand: leaf-and-gold, no wordmark */}
        <BrandMark size={40} to="/" data-testid={TESTIDS.header.wordmark} />

        {/* Desktop nav — from ≥1024 (lg) */}
        <nav aria-label="Primary" className="hidden lg:flex items-center gap-7 xl:gap-9">
          <Link
            to="/investors"
            data-testid={TESTIDS.pathway.navInvestor}
            onClick={() => setPathway("investor")}
            className="iv-nav-link"
          >
            Investors
          </Link>
          <Link
            to="/companies"
            data-testid={TESTIDS.pathway.navCompany}
            onClick={() => setPathway("company")}
            className="iv-nav-link"
          >
            Companies
          </Link>
          <span aria-hidden className="h-4 w-px bg-[var(--iv-line-strong)]" />
          {NAV.map((item) => {
            const anchor = item.href.replace(/^\/?#/, "");
            const isActive = active === anchor;
            return (
              <a
                key={item.label}
                href={item.href}
                data-testid={item.testid}
                onClick={handleAnchor(item.href)}
                aria-current={isActive ? "true" : undefined}
                className={`iv-nav-link ${isActive ? "iv-nav-link--active" : ""}`}
              >
                {item.label}
              </a>
            );
          })}
        </nav>

        {/* Desktop right actions */}
        <div className="hidden lg:flex items-center gap-3 xl:gap-4">
          <Link
            to="/login"
            data-testid={TESTIDS.header.ctaSignIn}
            className="iv-nav-signin"
          >
            Sign in
          </Link>
          <Link
            to="/access"
            data-testid={TESTIDS.header.ctaLive}
            onClick={() => {
              // Do NOT force a default pathway here; Access.jsx will render
              // the pathway picker when none is set. Silent defaulting to
              // investor was the P0 pathway-leak bug.
            }}
            className="iv-nav-cta"
          >
            Start analysis <ArrowRight size={14} strokeWidth={1.8} />
          </Link>
        </div>

        {/* Mobile toggle — never compresses the desktop nav */}
        <button
          ref={toggleRef}
          type="button"
          aria-label="Open navigation menu"
          aria-expanded={open}
          aria-controls="iv-mobile-nav"
          data-testid={TESTIDS.header.mobileToggle}
          onClick={() => setOpen(true)}
          className="iv-nav-mobile-toggle lg:hidden -mr-2 p-2"
        >
          <Menu size={22} strokeWidth={1.6} />
        </button>
      </div>

      {/* Mobile full-screen menu — rendered via portal so the header's
          backdrop-filter (which creates a containing block for fixed
          descendants) does not trap the overlay to the header's height. */}
      {createPortal(
        <div
          id="iv-mobile-nav"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          data-testid={TESTIDS.header.mobilePanel}
          className={`iv-mobile-nav ${open ? "iv-mobile-nav--open" : ""}`}
          aria-hidden={!open}
        >
          <div className="iv-mobile-nav__inner">
            <div className="flex items-center justify-between h-[68px] px-6">
              <BrandMark size={38} to="/" onClick={() => setOpen(false)} />
              <button
                ref={closeBtnRef}
                type="button"
                aria-label="Close navigation menu"
                data-testid={TESTIDS.header.mobileClose}
                onClick={closeMenu}
                className="iv-nav-mobile-close p-2 -mr-2"
              >
                <X size={22} strokeWidth={1.6} />
              </button>
            </div>
            <nav aria-label="Primary mobile" className="px-6 pt-6 pb-4 flex flex-col gap-1">
              <Link
                to="/investors"
                data-testid={`m-${TESTIDS.pathway.navInvestor}`}
                onClick={() => { setPathway("investor"); setOpen(false); }}
                className="iv-mobile-nav__link"
              >
                Investors
              </Link>
              <Link
                to="/companies"
                data-testid={`m-${TESTIDS.pathway.navCompany}`}
                onClick={() => { setPathway("company"); setOpen(false); }}
                className="iv-mobile-nav__link"
              >
                Companies
              </Link>
              <div aria-hidden className="h-px my-2 bg-[rgba(240,235,222,0.14)]" />
              {NAV.map((item, i) => (
                <a
                  key={item.label}
                  ref={i === 0 ? firstMobileLinkRef : undefined}
                  href={item.href}
                  data-testid={`m-${item.testid}`}
                  onClick={handleAnchor(item.href)}
                  className="iv-mobile-nav__link"
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="mt-auto px-6 pb-[max(24px,env(safe-area-inset-bottom))] pt-6 border-t border-[rgba(240,235,222,0.14)] flex flex-col gap-3">
              <Link
                to="/login"
                data-testid={`m-${TESTIDS.header.ctaSignIn}`}
                onClick={() => setOpen(false)}
                className="iv-mobile-nav__signin"
              >
                Sign in
              </Link>
              <Link
                to="/access"
                data-testid={`m-${TESTIDS.header.ctaLive}`}
                onClick={() => setOpen(false)}
                className="iv-mobile-nav__cta"
              >
                Start analysis <ArrowRight size={16} strokeWidth={1.8} />
              </Link>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}
